import React, { useEffect, useRef, useState } from "react";
import { Chip, IconButton, Tooltip, Typography } from "@material-ui/core";

import { makeStyles } from "@material-ui/styles";

import { AddCircleRounded, RemoveCircleRounded } from "@material-ui/icons";

import { AllowListProp } from "./PropsFilterSelect";
import { theme } from "../../../theme";

export type PropChipType = "user" | "group" | "event";

type PropChipsProps = {
  props: AllowListProp[];
  showNoneMessage?: boolean;
  onDelete?: (prop: AllowListProp) => void;
};

/**
 * Comparator function for sorting arrays of PropsFilterOption by their label.
 */
export const sortPropsFn = (a: AllowListProp, b: AllowListProp) => {
  return a.label.localeCompare(b.label);
};

export function colorForPropType(propType: PropChipType): string {
  switch (propType) {
    case "event":
      return theme.palette.success.light as string;
    case "group":
      return theme.palette.info.light as string;
    case "user":
      return theme.palette.secondary.light as string;
  }
}

const useStyles = makeStyles((theme) => ({
  chip: {
    color: "white",
    margin: "0.25em 0.25em 0.25em 0",
    maxWidth: theme.spacing(20),
  },
  grid: {
    maxHeight: "2em",
    overflow: "hidden",
    position: "relative" as "relative",
    whiteSpace: "nowrap" as "nowrap",
  },
  iconButton: {
    marginTop: "0.125em",
    padding: 0,
  },
  showMoreButton: {
    height: "100%",
    paddingRight: "0.125em",
    position: "absolute" as "absolute",
    right: 0,
    textAlign: "right",
    top: 0,
    width: "10em",
  },
}));

function PropChips({
  props,
  showNoneMessage = true,
  onDelete = undefined,
}: PropChipsProps): JSX.Element {
  const classes = useStyles();
  const gridRef = useRef<HTMLDivElement | null>(null);

  const [gridWidth, setGridWidth] = useState<number>(0);
  const [chipWidth, setChipWidth] = useState<number>(0);
  const [isShowingAll, setIsShowingAll] = useState<boolean>(false);

  useEffect(() => {
    if (gridRef.current === null) {
      return;
    }

    const gridWidth = gridRef.current?.getBoundingClientRect().width || 0;
    const chipWidths = Array.from(gridRef.current!.children).reduce(
      (acc, child) => {
        return acc + child.getBoundingClientRect().width;
      },
      0
    );

    setGridWidth(gridWidth);
    setChipWidth(chipWidths);
  }, [gridRef, setGridWidth, setChipWidth]);

  if (showNoneMessage && props.length === 0) {
    return (
      <Typography variant="body1">
        <em>None</em>
      </Typography>
    );
  }

  return (
    <div
      className={classes.grid}
      ref={gridRef}
      style={{
        maxHeight: isShowingAll ? "none" : "2em",
        whiteSpace: isShowingAll ? "inherit" : "nowrap",
      }}
    >
      {props.map((prop) => (
        <Tooltip
          title={prop.label}
          key={`${prop.type}-props-chip-${prop.label}`}
        >
          <Chip
            className={classes.chip}
            label={prop.label}
            size="small"
            style={{
              backgroundColor: colorForPropType(prop.type),
            }}
            onDelete={onDelete ? () => onDelete(prop) : undefined}
          />
        </Tooltip>
      ))}

      {chipWidth > gridWidth && (
        <div
          className={classes.showMoreButton}
          style={{
            background: isShowingAll
              ? "transparent"
              : "linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
            width: isShowingAll ? "2em" : "10em",
          }}
        >
          <Tooltip
            title={isShowingAll ? "Show Less" : `Show All ${props.length}`}
          >
            <IconButton
              className={classes.iconButton}
              onClick={() => setIsShowingAll(!isShowingAll)}
            >
              {isShowingAll ? <RemoveCircleRounded /> : <AddCircleRounded />}
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default PropChips;
